import { uploadAssignmentFile } from '../../services/professorService';
import React, { useState, useRef } from 'react';
import {
  Plus,
  ChevronDown,
  ChevronRight,
  FileText,
  Video,
  Link as LinkIcon,
  Presentation,
  BookOpen,
  Image,
  Archive,
  File,
  Trash2,
  Download,
  Upload,
  Folder,
  FolderOpen,
  Eye,
  Loader2,
  AlertCircle,
  ExternalLink,
  Globe,
  X,
  Edit2,
  Check,
  GripVertical,
  Send,
} from 'lucide-react';
import {
  useCourseContent,
  useCreateChapter,
  useUpdateChapter,
  useDeleteChapter,
  useCreateMaterial,
  useDeleteMaterial,
  useUpdateContentSettings,
  useReorderChapters,
} from '../../hooks/useProfessor';
import type {
  ChapterResponse,
  MaterialResponse,
  CourseMaterialType,
  CreateMaterialDto,
} from '@/types/professor.types';
import { CuteConfirmModal } from '@/shared/components/CuteConfirmModal';
import { getFullFileUrl, getDownloadFileUrl } from '@/utils/fileUrl';

interface ContentTabProps {
  groupeId: string;
  moduleElementId: string;
  // Year being consulted; omitted = the live one
  academicYearId?: string;
}

// Material type configuration with icons and colors
const materialTypeConfig: Record<
  CourseMaterialType,
  { icon: React.ElementType; color: string; label: string }
> = {
  DOCUMENT: { icon: FileText, color: 'text-blue-600 bg-blue-50', label: 'Document' },
  VIDEO: { icon: Video, color: 'text-red-600 bg-red-50', label: 'Vidéo' },
  LINK: { icon: LinkIcon, color: 'text-green-600 bg-green-50', label: 'Lien' },
  PRESENTATION: { icon: Presentation, color: 'text-purple-600 bg-purple-50', label: 'Présentation' },
  EXERCISE: { icon: BookOpen, color: 'text-amber-600 bg-amber-50', label: 'Exercice' },
  OTHER: { icon: File, color: 'text-gray-600 bg-gray-50', label: 'Autre' },
};

// File extension to material type mapping
const getFileType = (fileName: string): CourseMaterialType => {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  if (['pdf', 'doc', 'docx', 'txt', 'rtf'].includes(ext)) return 'DOCUMENT';
  if (['ppt', 'pptx', 'key', 'odp'].includes(ext)) return 'PRESENTATION';
  if (['mp4', 'avi', 'mov', 'mkv', 'webm'].includes(ext)) return 'VIDEO';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'OTHER';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'DOCUMENT';
  if (['zip', 'rar', '7z', 'tar'].includes(ext)) return 'OTHER';
  return 'OTHER';
};

// File icon based on extension
const getFileIcon = (fileName: string | null, materialType: CourseMaterialType) => {
  if (!fileName) {
    const config = materialTypeConfig[materialType];
    return config.icon;
  }

  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return Image;
  if (['zip', 'rar', '7z', 'tar'].includes(ext)) return Archive;

  return materialTypeConfig[materialType].icon;
};

// Format file size
const formatFileSize = (bytes: number | null) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Get file extension
const getFileExtension = (fileName: string | null): string => {
  if (!fileName) return '';
  return fileName.split('.').pop()?.toLowerCase() || '';
};

const ContentTab: React.FC<ContentTabProps> = ({ groupeId, moduleElementId, academicYearId }) => {
  // groupeId is passed from parent but not directly used in this component
  void groupeId;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expandedChapters, setExpandedChapters] = useState<Set<string>>(new Set());
  const [showCreateChapter, setShowCreateChapter] = useState(false);
  const [showAddMaterial, setShowAddMaterial] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Drag & drop state for chapters
  const [draggedChapter, setDraggedChapter] = useState<string | null>(null);
  const [dragOverChapter, setDragOverChapter] = useState<string | null>(null);

  // Delete confirmation modals
  const [deleteChapterModal, setDeleteChapterModal] = useState<{ open: boolean; chapter: ChapterResponse | null }>({
    open: false,
    chapter: null,
  });
  const [deleteMaterialModal, setDeleteMaterialModal] = useState<{ open: boolean; material: MaterialResponse | null }>({
    open: false,
    material: null,
  });

  // Preview modal
  const [previewModal, setPreviewModal] = useState<{ open: boolean; material: MaterialResponse | null }>({
    open: false,
    material: null,
  });

  // Editable general section title
  const [isEditingSectionTitle, setIsEditingSectionTitle] = useState(false);
  const [editedSectionTitle, setEditedSectionTitle] = useState('');

  // New chapter/section form
  const [newChapterTitle, setNewChapterTitle] = useState('');
  const [newChapterDescription, setNewChapterDescription] = useState('');
  const [newChapterIsSection, setNewChapterIsSection] = useState(false);

  // New material form
  const [newMaterial, setNewMaterial] = useState<{
    type: 'file' | 'link';
    title: string;
    description: string;
    externalUrl: string;
    file: File | null;
  }>({
    type: 'file',
    title: '',
    description: '',
    externalUrl: '',
    file: null,
  });

  // Queries and mutations
  const { data: content, isLoading, error, refetch } = useCourseContent(groupeId, moduleElementId, academicYearId);
  const createChapterMutation = useCreateChapter();
  const updateChapterMutation = useUpdateChapter();
  const deleteChapterMutation = useDeleteChapter();
  const createMaterialMutation = useCreateMaterial();
  const deleteMaterialMutation = useDeleteMaterial();
  const updateContentSettingsMutation = useUpdateContentSettings();
  const reorderChaptersMutation = useReorderChapters();

  // Toggle chapter expansion
  const toggleChapter = (chapterId: string) => {
    setExpandedChapters((prev) => {
      const next = new Set(prev);
      if (next.has(chapterId)) {
        next.delete(chapterId);
      } else {
        next.add(chapterId);
      }
      return next;
    });
  };

  // Drag & drop handlers for chapter reordering
  const handleChapterDragStart = (e: React.DragEvent, chapterId: string) => {
    e.dataTransfer.effectAllowed = 'move';
    setDraggedChapter(chapterId);
  };

  const handleChapterDragOver = (e: React.DragEvent, chapterId: string) => {
    e.preventDefault();
    if (draggedChapter && draggedChapter !== chapterId) {
      setDragOverChapter(chapterId);
    }
  };

  const handleChapterDragLeave = () => {
    setDragOverChapter(null);
  };

  const handleChapterDrop = async (e: React.DragEvent, targetChapterId: string) => {
    e.preventDefault();
    if (!draggedChapter || !content?.chapters) return;

    const chapters = [...content.chapters];
    const draggedIndex = chapters.findIndex(c => c.id === draggedChapter);
    const targetIndex = chapters.findIndex(c => c.id === targetChapterId);

    if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) {
      setDraggedChapter(null);
      setDragOverChapter(null);
      return;
    }

    // Reorder the array
    const [removed] = chapters.splice(draggedIndex, 1);
    chapters.splice(targetIndex, 0, removed);

    // Get the new order of chapter IDs
    const newChapterIds = chapters.map(c => c.id);

    try {
      await reorderChaptersMutation.mutateAsync({
        groupeId,
        moduleElementId,
        chapterIds: newChapterIds,
      });
      refetch();
    } catch (error) {
      console.error('Failed to reorder chapters:', error);
    }

    setDraggedChapter(null);
    setDragOverChapter(null);
  };

  const handleChapterDragEnd = () => {
    setDraggedChapter(null);
    setDragOverChapter(null);
  };

  // Create new chapter or section (starts as draft)
  const handleCreateChapter = async () => {
    if (!newChapterTitle.trim()) return;

    try {
      await createChapterMutation.mutateAsync({
        groupeId,
        moduleElementId,
        dto: {
          title: newChapterTitle.trim(),
          description: newChapterDescription.trim() || undefined,
          isPublished: false, // New chapters start as draft
          isSection: newChapterIsSection, // true for custom sections
        },
      });
      setNewChapterTitle('');
      setNewChapterDescription('');
      setNewChapterIsSection(false);
      setShowCreateChapter(false);
      refetch();
    } catch (error) {
      console.error('Failed to create chapter:', error);
    }
  };

  // Toggle chapter publish status
  const handleToggleChapterPublish = async (chapter: ChapterResponse) => {
    try {
      await updateChapterMutation.mutateAsync({
        chapterId: chapter.id,
        groupeId,
        moduleElementId,
        dto: { isPublished: !chapter.isPublished },
      });
      refetch();
    } catch (error) {
      console.error('Failed to update chapter:', error);
    }
  };

  // Delete chapter (with cute modal)
  const handleDeleteChapter = async () => {
    if (!deleteChapterModal.chapter) return;

    try {
      await deleteChapterMutation.mutateAsync({
        chapterId: deleteChapterModal.chapter.id,
        groupeId,
        moduleElementId,
      });
      setDeleteChapterModal({ open: false, chapter: null });
      refetch();
    } catch (error) {
      console.error('Failed to delete chapter:', error);
      setDeleteChapterModal({ open: false, chapter: null });
    }
  };

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setNewMaterial({
        ...newMaterial,
        file,
        title: newMaterial.title || file.name.replace(/\.[^/.]+$/, ''),
      });
    }
  };

  // Create new material
  const handleCreateMaterial = async (chapterId: string | null) => {
    if (newMaterial.type === 'file' && !newMaterial.file) return;
    if (newMaterial.type === 'link' && !newMaterial.externalUrl.trim()) return;
    if (!newMaterial.title.trim()) return;

    setIsUploading(true);

    try {
      let dto: CreateMaterialDto = {
        chapterId: chapterId || undefined,
        title: newMaterial.title.trim(),
        description: newMaterial.description.trim() || undefined,
        isPublished: true, // Materials inherit visibility from chapter
      };

      if (newMaterial.type === 'file' && newMaterial.file) {
        // Stored file, not an inline base64 string (mobile cannot open those).
        const uploaded = await uploadAssignmentFile(newMaterial.file);

        dto = {
          ...dto,
          fileName: newMaterial.file.name,
          fileUrl: uploaded.file.url,
          fileType: newMaterial.file.type,
          fileSize: newMaterial.file.size,
          materialType: getFileType(newMaterial.file.name),
        };
      } else if (newMaterial.type === 'link') {
        dto = {
          ...dto,
          externalUrl: newMaterial.externalUrl.trim(),
          materialType: 'LINK',
        };
      }

      await createMaterialMutation.mutateAsync({
        groupeId,
        moduleElementId,
        dto,
      });

      setNewMaterial({
        type: 'file',
        title: '',
        description: '',
        externalUrl: '',
        file: null,
      });
      setShowAddMaterial(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      refetch();
    } catch (error) {
      console.error('Failed to create material:', error);
    } finally {
      setIsUploading(false);
    }
  };

  // Delete material (with cute modal)
  const handleDeleteMaterial = async () => {
    if (!deleteMaterialModal.material) return;

    try {
      await deleteMaterialMutation.mutateAsync({
        materialId: deleteMaterialModal.material.id,
        groupeId,
        moduleElementId,
      });
      setDeleteMaterialModal({ open: false, material: null });
      refetch();
    } catch (error) {
      console.error('Failed to delete material:', error);
      setDeleteMaterialModal({ open: false, material: null });
    }
  };

  // Start editing section title
  const handleStartEditingSectionTitle = () => {
    setEditedSectionTitle(content?.generalSectionTitle || 'Ressources générales');
    setIsEditingSectionTitle(true);
  };

  // Save section title
  const handleSaveSectionTitle = async () => {
    if (!editedSectionTitle.trim()) return;

    try {
      await updateContentSettingsMutation.mutateAsync({
        groupeId,
        moduleElementId,
        generalSectionTitle: editedSectionTitle.trim(),
      });
      setIsEditingSectionTitle(false);
      refetch();
    } catch (error) {
      console.error('Failed to update section title:', error);
    }
  };

  // Cancel editing section title
  const handleCancelEditingSectionTitle = () => {
    setIsEditingSectionTitle(false);
    setEditedSectionTitle('');
  };

  // Render publish toggle button - cleaner than badge
  const renderPublishToggle = (chapter: ChapterResponse) => {
    const isLoading = updateChapterMutation.isPending;

    if (chapter.isPublished) {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleToggleChapterPublish(chapter);
          }}
          disabled={isLoading}
          className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
          title="Cliquez pour masquer ce chapitre aux étudiants"
        >
          <Globe className="h-3.5 w-3.5" />
          <span>Visible</span>
        </button>
      );
    }
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleToggleChapterPublish(chapter);
        }}
        disabled={isLoading}
        className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-600 border border-gray-300 rounded-lg text-sm font-medium hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300 transition-colors disabled:opacity-50"
        title="Cliquez pour publier ce chapitre"
      >
        <Send className="h-3.5 w-3.5" />
        <span>Publier</span>
      </button>
    );
  };

  // Render material item (no individual publish control)
  const renderMaterial = (material: MaterialResponse) => {
    const Icon = getFileIcon(material.fileName, material.materialType);
    const config = materialTypeConfig[material.materialType];

    return (
      <div
        key={material.id}
        className="flex items-center gap-3 px-4 py-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
      >
        <div className={`p-2 rounded-lg ${config.color}`}>
          <Icon className="h-4 w-4" />
        </div>

        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 truncate">{material.title}</h4>
          {material.description && (
            <p className="text-sm text-gray-500 truncate">{material.description}</p>
          )}
          <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
            <span>{config.label}</span>
            {material.fileSize && (
              <>
                <span>•</span>
                <span>{formatFileSize(material.fileSize)}</span>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          {material.externalUrl && (
            <a
              href={material.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Ouvrir le lien"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
          {material.fileUrl && (
            <>
              <button
                onClick={() => setPreviewModal({ open: true, material })}
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Aperçu"
              >
                <Eye className="h-4 w-4" />
              </button>
              <a
                href={getDownloadFileUrl(material.fileUrl)}
                download={material.fileName || 'document'}
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Télécharger"
              >
                <Download className="h-4 w-4" />
              </a>
            </>
          )}
          <button
            onClick={() => setDeleteMaterialModal({ open: true, material })}
            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Supprimer"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  // Render add material form
  const renderAddMaterialForm = (chapterId: string | null) => {
    const targetId = chapterId || 'general';
    if (showAddMaterial !== targetId) return null;

    return (
      <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="font-medium text-gray-900 mb-4">Ajouter un élément</h4>

        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setNewMaterial({ ...newMaterial, type: 'file' })}
            className={`flex-1 py-2 px-4 rounded-lg border transition-colors ${
              newMaterial.type === 'file'
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
          >
            <Upload className="h-4 w-4 inline-block mr-2" />
            Fichier
          </button>
          <button
            onClick={() => setNewMaterial({ ...newMaterial, type: 'link' })}
            className={`flex-1 py-2 px-4 rounded-lg border transition-colors ${
              newMaterial.type === 'link'
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
          >
            <LinkIcon className="h-4 w-4 inline-block mr-2" />
            Lien externe
          </button>
        </div>

        {newMaterial.type === 'file' && (
          <div className="mb-4">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.mp4,.mov,.avi,.jpg,.jpeg,.png,.gif,.zip,.rar"
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 transition-colors"
            >
              {newMaterial.file ? (
                <div className="flex items-center justify-center gap-2">
                  <FileText className="h-6 w-6 text-blue-600" />
                  <span className="font-medium text-gray-900">{newMaterial.file.name}</span>
                  <span className="text-sm text-gray-500">
                    ({formatFileSize(newMaterial.file.size)})
                  </span>
                </div>
              ) : (
                <>
                  <Upload className="mx-auto h-8 w-8 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">
                    Cliquez pour sélectionner un fichier
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    PDF, DOC, PPT, XLS, MP4, images, ZIP (max 50MB)
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {newMaterial.type === 'link' && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
            <input
              type="url"
              value={newMaterial.externalUrl}
              onChange={(e) => setNewMaterial({ ...newMaterial, externalUrl: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="https://..."
            />
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Titre {newMaterial.type === 'link' && '*'}
          </label>
          <input
            type="text"
            value={newMaterial.title}
            onChange={(e) => setNewMaterial({ ...newMaterial, title: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder={newMaterial.type === 'file' ? 'Titre (optionnel - sinon nom du fichier)' : 'Titre du lien'}
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description (optionnel)
          </label>
          <textarea
            value={newMaterial.description}
            onChange={(e) => setNewMaterial({ ...newMaterial, description: e.target.value })}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Description du document..."
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={() => {
              setShowAddMaterial(null);
              setNewMaterial({
                type: 'file',
                title: '',
                description: '',
                externalUrl: '',
                file: null,
              });
            }}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={() => handleCreateMaterial(chapterId)}
            disabled={
              isUploading ||
              (newMaterial.type === 'file' && !newMaterial.file) ||
              (newMaterial.type === 'link' && (!newMaterial.externalUrl.trim() || !newMaterial.title.trim()))
            }
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {isUploading && <Loader2 className="h-4 w-4 animate-spin" />}
            Ajouter
          </button>
        </div>
      </div>
    );
  };

  // Render preview modal
  const renderPreviewModal = () => {
    if (!previewModal.open || !previewModal.material) return null;

    const material = previewModal.material;
    const ext = getFileExtension(material.fileName);
    const isPdf = ext === 'pdf';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
    const isVideo = ['mp4', 'webm', 'mov'].includes(ext);
    const canShowPreview = isPdf || isImage || isVideo;
    const Icon = getFileIcon(material.fileName, material.materialType);
    const config = materialTypeConfig[material.materialType];

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden">
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${config.color}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{material.title}</h3>
                <p className="text-xs text-gray-500">
                  {material.fileName && `${material.fileName} • `}
                  {formatFileSize(material.fileSize)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {material.fileUrl && (
                <a
                  href={getDownloadFileUrl(material.fileUrl)}
                  download={material.fileName || 'document'}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Télécharger
                </a>
              )}
              <button
                onClick={() => setPreviewModal({ open: false, material: null })}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Preview Content */}
          <div className="flex-1 overflow-hidden bg-gray-100">
            {canShowPreview && material.fileUrl ? (
              <>
                {isPdf && (
                  <iframe
                    src={getFullFileUrl(material.fileUrl)}
                    className="w-full h-full"
                    title={material.title}
                  />
                )}
                {isImage && (
                  <div className="w-full h-full flex items-center justify-center p-4">
                    <img
                      src={getFullFileUrl(material.fileUrl)}
                      alt={material.title}
                      className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                    />
                  </div>
                )}
                {isVideo && (
                  <div className="w-full h-full flex items-center justify-center p-4">
                    <video
                      src={getFullFileUrl(material.fileUrl)}
                      controls
                      className="max-w-full max-h-full rounded-lg shadow-lg"
                    >
                      Votre navigateur ne supporte pas la lecture vidéo.
                    </video>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <File className="h-16 w-16 mb-4 text-gray-400" />
                <p className="text-lg font-medium">Aperçu non disponible</p>
                <p className="text-sm mt-1">
                  Ce type de fichier ({ext.toUpperCase() || 'inconnu'}) ne peut pas être prévisualisé.
                </p>
                {material.fileUrl && (
                  <a
                    href={getDownloadFileUrl(material.fileUrl)}
                    download={material.fileName || 'document'}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    Télécharger pour visualiser
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
        <AlertCircle className="h-5 w-5" />
        <span>Erreur lors du chargement du contenu</span>
        <button onClick={() => refetch()} className="ml-auto text-sm underline">
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Contenu du cours</h3>
            <p className="text-sm text-gray-500 mt-1">
              Organisez le contenu de votre cours en chapitres. Un chapitre peut aussi regrouper des
              ressources hors progression (bonus, lectures complémentaires…).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setNewChapterIsSection(false);
                setShowCreateChapter(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Nouveau chapitre
            </button>
          </div>
        </div>

        {/* Info message */}
        <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
          <p className="text-sm text-blue-800">
            <AlertCircle className="h-4 w-4 inline-block mr-1" />
            Le contenu ne sera visible aux étudiants qu'une fois publié.
          </p>
        </div>

        {/* Create chapter/section form */}
        {showCreateChapter && (
          <div className={`p-4 border-b ${newChapterIsSection ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'}`}>
            <h4 className={`font-medium mb-3 ${newChapterIsSection ? 'text-purple-900' : 'text-gray-900'}`}>
              {newChapterIsSection ? 'Nouvelle section personnalisée' : 'Nouveau chapitre'}
            </h4>
            <div className="space-y-3">
              <input
                type="text"
                value={newChapterTitle}
                onChange={(e) => setNewChapterTitle(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 ${
                  newChapterIsSection
                    ? 'border-purple-300 focus:ring-purple-500 focus:border-purple-500'
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder={newChapterIsSection ? 'Titre de la section (ex: Ressources supplémentaires)' : 'Titre du chapitre (ex: Chapitre 1: Introduction)'}
                autoFocus
              />
              <textarea
                value={newChapterDescription}
                onChange={(e) => setNewChapterDescription(e.target.value)}
                rows={2}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 ${
                  newChapterIsSection
                    ? 'border-purple-300 focus:ring-purple-500 focus:border-purple-500'
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder="Description (optionnel)"
              />
              {newChapterIsSection && (
                <p className="text-sm text-purple-600">
                  Les sections sont idéales pour regrouper des ressources qui ne font pas partie de la progression du cours (ex: lectures additionnelles, bonus, etc.)
                </p>
              )}
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowCreateChapter(false);
                    setNewChapterTitle('');
                    setNewChapterDescription('');
                    setNewChapterIsSection(false);
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreateChapter}
                  disabled={!newChapterTitle.trim() || createChapterMutation.isPending}
                  className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 transition-colors flex items-center gap-2 ${
                    newChapterIsSection
                      ? 'bg-purple-600 hover:bg-purple-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {createChapterMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Créer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Chapters */}
        <div className="p-4 space-y-4">
          {content?.chapters.length === 0 && content?.generalMaterials.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="mx-auto h-12 w-12 text-gray-400" />
              <h4 className="mt-4 text-lg font-medium text-gray-900">Aucun contenu</h4>
              <p className="mt-2 text-sm text-gray-500">
                Commencez par créer un chapitre pour organiser votre contenu
              </p>
            </div>
          ) : (
            <>
              {/* Chapters and Sections list */}
              {content?.chapters.map((chapter) => {
                const isExpanded = expandedChapters.has(chapter.id);
                const isDragging = draggedChapter === chapter.id;
                const isDragOver = dragOverChapter === chapter.id;
                const isSection = chapter.isSection; // Custom section vs regular chapter

                // Color schemes based on type (section vs chapter) and published status
                const getContainerClasses = () => {
                  if (isSection) {
                    // Purple theme for sections
                    return chapter.isPublished
                      ? 'border-purple-200 bg-gradient-to-r from-purple-50/30 to-white'
                      : 'border-purple-300 bg-gradient-to-r from-purple-100/50 to-purple-50/30';
                  }
                  // Blue/amber theme for chapters
                  return chapter.isPublished
                    ? 'border-gray-200 bg-white'
                    : 'border-amber-200 bg-gradient-to-r from-amber-50/50 to-transparent';
                };

                const getHeaderClasses = () => {
                  if (isSection) {
                    return chapter.isPublished
                      ? 'bg-purple-50/50 hover:bg-purple-100/50'
                      : 'bg-purple-100/50 hover:bg-purple-150/50';
                  }
                  return chapter.isPublished
                    ? 'bg-gray-50 hover:bg-gray-100'
                    : 'bg-amber-50/50 hover:bg-amber-100/50';
                };

                const getFolderColor = () => {
                  if (isSection) {
                    return 'text-purple-600';
                  }
                  return chapter.isPublished ? 'text-blue-600' : 'text-amber-500';
                };

                return (
                  <div
                    key={chapter.id}
                    draggable
                    onDragStart={(e) => handleChapterDragStart(e, chapter.id)}
                    onDragOver={(e) => handleChapterDragOver(e, chapter.id)}
                    onDragLeave={handleChapterDragLeave}
                    onDrop={(e) => handleChapterDrop(e, chapter.id)}
                    onDragEnd={handleChapterDragEnd}
                    className={`border rounded-xl overflow-hidden transition-all ${
                      isDragging ? 'opacity-50 scale-[0.98]' : ''
                    } ${isDragOver ? 'border-blue-400 ring-2 ring-blue-200' : ''} ${getContainerClasses()}`}
                  >
                    {/* Chapter/Section header */}
                    <div className={`flex items-center gap-2 px-3 py-3 cursor-pointer transition-colors ${getHeaderClasses()}`}>
                      {/* Drag handle */}
                      <div
                        className="p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
                        title="Glisser pour réordonner"
                      >
                        <GripVertical className="h-4 w-4" />
                      </div>

                      {/* Expand/collapse button */}
                      <button
                        className="text-gray-400 hover:text-gray-600"
                        onClick={() => toggleChapter(chapter.id)}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5" />
                        ) : (
                          <ChevronRight className="h-5 w-5" />
                        )}
                      </button>

                      <Folder className={`h-5 w-5 ${getFolderColor()}`} />

                      <div className="flex-1 min-w-0" onClick={() => toggleChapter(chapter.id)}>
                        <div className="flex items-center gap-3">
                          <h4 className="font-medium text-gray-900">{chapter.title}</h4>
                          {isSection && (
                            <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                              Section
                            </span>
                          )}
                          <span className="text-sm text-gray-500">
                            ({chapter.materials.length} élément{chapter.materials.length !== 1 ? 's' : ''})
                          </span>
                        </div>
                        {chapter.description && (
                          <p className="text-sm text-gray-500 truncate">{chapter.description}</p>
                        )}
                      </div>

                      {/* Publish toggle button */}
                      {renderPublishToggle(chapter)}

                      {/* Delete action */}
                      <button
                        onClick={() => setDeleteChapterModal({ open: true, chapter })}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Chapter/Section content (expanded) */}
                    {isExpanded && (
                      <div className="p-4 space-y-3 bg-white">
                        {chapter.materials.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">
                            {isSection ? 'Aucune ressource dans cette section' : 'Aucun élément dans ce chapitre'}
                          </p>
                        ) : (
                          chapter.materials.map((material) => renderMaterial(material))
                        )}

                        {showAddMaterial !== chapter.id && (
                          <button
                            onClick={() => setShowAddMaterial(chapter.id)}
                            className={`w-full py-2 border-2 border-dashed rounded-lg transition-colors flex items-center justify-center gap-2 ${
                              isSection
                                ? 'border-purple-300 text-purple-500 hover:border-purple-400 hover:text-purple-600'
                                : 'border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600'
                            }`}
                          >
                            <Plus className="h-4 w-4" />
                            {isSection ? 'Ajouter une ressource' : 'Ajouter un élément'}
                          </button>
                        )}

                        {renderAddMaterialForm(chapter.id)}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* General materials section - always visible when there are materials */}
              {content?.generalMaterials && content.generalMaterials.length > 0 && (
                <div className="border border-purple-200 rounded-xl overflow-hidden bg-gradient-to-r from-purple-50/30 to-transparent">
                  <div className="px-4 py-3 bg-purple-50/50 border-b border-purple-200">
                    {isEditingSectionTitle ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editedSectionTitle}
                          onChange={(e) => setEditedSectionTitle(e.target.value)}
                          className="flex-1 px-3 py-1.5 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm font-medium bg-white"
                          autoFocus
                          placeholder="Nom de la section..."
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveSectionTitle();
                            if (e.key === 'Escape') handleCancelEditingSectionTitle();
                          }}
                        />
                        <button
                          onClick={handleSaveSectionTitle}
                          disabled={updateContentSettingsMutation.isPending}
                          className="p-1.5 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                          title="Enregistrer"
                        >
                          {updateContentSettingsMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={handleCancelEditingSectionTitle}
                          className="p-1.5 text-gray-500 hover:bg-gray-200 rounded-lg transition-colors"
                          title="Annuler"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={handleStartEditingSectionTitle}
                        className="flex items-center gap-2 group w-full text-left"
                        title="Cliquez pour renommer cette section"
                      >
                        <Folder className="h-5 w-5 text-purple-600" />
                        <h4 className="font-medium text-gray-800 group-hover:text-purple-700 transition-colors">
                          {content?.generalSectionTitle || 'Ressources générales'}
                        </h4>
                        <Edit2 className="h-3.5 w-3.5 text-gray-400 group-hover:text-purple-600 transition-colors" />
                        <span className="text-xs text-gray-400 ml-auto">Cliquez pour renommer</span>
                      </button>
                    )}
                  </div>
                  <div className="p-4 space-y-3">
                    {content?.generalMaterials.map((material) => renderMaterial(material))}

                    {showAddMaterial !== 'general' && (
                      <button
                        onClick={() => setShowAddMaterial('general')}
                        className="w-full py-2 border-2 border-dashed border-purple-300 rounded-lg text-purple-500 hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-colors flex items-center justify-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Ajouter une ressource
                      </button>
                    )}

                    {renderAddMaterialForm(null)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>


      {/* Delete Chapter Confirmation Modal */}
      <CuteConfirmModal
        isOpen={deleteChapterModal.open}
        title="Supprimer le chapitre"
        message="Les documents de ce chapitre seront déplacés vers les ressources générales."
        itemName={deleteChapterModal.chapter?.title}
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        variant="danger"
        onConfirm={handleDeleteChapter}
        onCancel={() => setDeleteChapterModal({ open: false, chapter: null })}
      />

      {/* Delete Material Confirmation Modal */}
      <CuteConfirmModal
        isOpen={deleteMaterialModal.open}
        title="Supprimer le document"
        message="Cette action est irréversible. Le document sera définitivement supprimé."
        itemName={deleteMaterialModal.material?.title}
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        variant="danger"
        onConfirm={handleDeleteMaterial}
        onCancel={() => setDeleteMaterialModal({ open: false, material: null })}
      />

      {/* Preview Modal */}
      {renderPreviewModal()}
    </div>
  );
};

export default ContentTab;
